import React, { useState } from "react";
import { Image as ImageIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EditIcon, Delete02Icon, PlusIcon } from "../../../../../assets/icons";
import DeletePopup from "../../../../../components/delete-popup";
import { deletePortfolio } from "../../../../../redux/slice/companySlice";
import PreviewModal from "../../../../../components/modals/previewModal";
import { usePermission } from "../../../../../hooks/usePermission";
import { routes } from "../../../../../routes/pageRoutes";
import { useToast } from "../../../../../context/TostContext";

const MODULE = "company_management";

const ImageSwiper = ({ media, title, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!media || media.length === 0) return null;

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const currentMedia = media[currentIndex];

  return (
    <div
      className="position-relative rounded-3 overflow-hidden border shadow-sm"
      style={{ width: "80px", height: "80px", flexShrink: 0, backgroundColor: "#f8f9fa" }}
    >
      {currentMedia?.mimetype === "video/mp4" ?
        <video src={currentMedia.file?.url || currentMedia.url}
          alt={title || "Post Media"}
          className="w-100 h-100"
          style={{ objectFit: "cover" }} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onImageClick) onImageClick(currentIndex);
          }} /> :
        <img
          src={currentMedia.file?.url || currentMedia.url}
          alt={title || "Portfolio Media"}
          className="w-100 h-100"
          style={{ objectFit: "cover", cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onImageClick) onImageClick(currentIndex);
          }}
          onError={(e) => {
            e.target.src = "/images/image-placeholder.png";
          }}
        />}

      {media.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="position-absolute top-50 start-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
            style={{ width: "16px", height: "16px", borderRadius: "0 4px 4px 0", cursor: "pointer", fontSize: "10px" }}
          >
            &#10094;
          </button>
          <button
            onClick={handleNext}
            className="position-absolute top-50 end-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
            style={{ width: "16px", height: "16px", borderRadius: "4px 0 0 4px", cursor: "pointer", fontSize: "10px" }}
          >
            &#10095;
          </button>
          <div className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-1 mb-1">
            {media.map((_, idx) => (
              <div
                key={idx}
                className="rounded-circle"
                style={{
                  width: "3px",
                  height: "3px",
                  backgroundColor: currentIndex === idx ? "white" : "rgba(255,255,255,0.5)",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Portfolios = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMediaArray, setPreviewMediaArray] = useState([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

  const { companyDetail, portfolioActionLoading } = useSelector((state) => state.company);

  const handleDeletePortfolio = () => {
    dispatch(deletePortfolio({ companyId: params.id, id: itemToDelete }))
      .unwrap()
      .then(() => {
        toast.success("Portfolio deleted successfully");
        setShowDeletePopup(false);
        setItemToDelete(null);
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to delete portfolio");
      });
  };

  return (
    <div className="position-relative h-100">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="fw-bold mb-0" style={{ fontSize: "16px", color: "#444050" }}>
          Portfolios
        </h5>
        {hasPermission(MODULE, "update") && (
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => {
              navigate(routes.addPortfolio.replace(":companyId", params.id));
            }}
          >
            <PlusIcon size={14} />
            Add Portfolio
          </button>
        )}
      </div>

      {companyDetail?.portfolios?.length > 0 ? (
        <div className="row g-3">
          {companyDetail.portfolios.map((portfolio, index) => (
            <div key={index} className="col-12 col-md-6">
              <div className="p-3 border rounded-3 bg-light-subtle position-relative custom-card h-100" style={{ paddingRight: "75px" }}>
                <div className="position-absolute top-0 end-0 p-2 d-flex gap-2">
                  {hasPermission(MODULE, "update") && (
                    <button
                      className="post-action-btn edit-btn border rounded p-1 bg-white"
                      onClick={() => {
                        navigate(
                          routes.viewPortfolio
                            .replace(":companyId", params.id)
                            .replace(":id", portfolio.id)
                        );
                      }}
                    >
                      <EditIcon width={12} height={12} color="#007bff" />
                    </button>
                  )}
                  {hasPermission(MODULE, "delete") && (
                    <button
                      className="post-action-btn delete-btn border rounded p-1 bg-white"
                      onClick={() => {
                        setItemToDelete(portfolio.id);
                        setShowDeletePopup(true);
                      }}
                    >
                      <Delete02Icon width={12} height={12} color="#FF0000" />
                    </button>
                  )}
                </div>
                <div className="d-flex gap-3 align-items-start">
                  {portfolio.media?.length > 0 ? (
                    <ImageSwiper
                      media={portfolio.media}
                      title={portfolio.title}
                      onImageClick={(idx) => {
                        const mediaArray = portfolio.media.map((item) => ({
                          url: item.file?.url || item.url,
                          title: portfolio.title || "Portfolio Image",
                          type: "image",
                        }));
                        setPreviewMediaArray(mediaArray);
                        setPreviewInitialIndex(idx);
                        setShowPreviewModal(true);
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center border rounded-3"
                      style={{ width: "80px", height: "80px", flexShrink: 0, backgroundColor: "#f8f9fa" }}
                    >
                      <ImageIcon size={24} color="#adb5bd" />
                    </div>
                  )}
                  <div className="flex-grow-1 overflow-hidden" style={{ paddingRight: "75px" }}>
                    <h6 className="fw-semibold text-dark mb-1 text-break" style={{ fontSize: "14px" }}>
                      {portfolio.title || "-"}
                    </h6>
                    {portfolio.category && (
                      <span className="badge bg-secondary-subtle text-secondary mb-2 small text-capitalize">
                        {portfolio.category}
                      </span>
                    )}
                    {portfolio.project_url && (
                      <div className="mb-2">
                        <a
                          href={portfolio.project_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary small text-decoration-none text-truncate d-block"
                        >
                          {portfolio.project_url}
                        </a>
                      </div>
                    )}
                    {portfolio.description && (
                      <p className="text-secondary text-break mb-0 small" style={{ lineHeight: "1.5" }}>
                        {portfolio.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted text-center py-4 small">No portfolios listed.</p>
      )}



      <DeletePopup
        show={showDeletePopup}
        onHide={() => {
          setShowDeletePopup(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeletePortfolio}
        content="Are you sure you want to delete this portfolio? This action cannot be undone."
        saveTitle="Delete"
        disabled={portfolioActionLoading}
      />

      <PreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        previewMedia={previewMediaArray}
        initialIndex={previewInitialIndex}
      />
    </div>
  );
};

export default Portfolios;
